import { useCallback } from 'react';

import AddressInput from '@perpay-web/components/base/AddressInput/AddressInput';
import { getErrors, getIsLoading } from '@perpay-web/data-module/useDataModule';
import {
    useAddressDetailsContext,
    getAddressDetails,
} from '@perpay-web/hooks/useAddressDetailsContext';
import {
    useAddressSuggestionsContext,
    getAddressSuggestions,
} from '@perpay-web/hooks/useAddressSuggestionsContext';

/**
 * Address Input Container Component
 * @param {Object} props - Component props
 * @param {string} props.addressInputText - Address input text
 * @param {string} [props.inputLabel] - Input label (optional)
 * @param {string} [props.className] - CSS class name (optional)
 * @param {Object} [props.errors={}] - Error object (optional)
 * @param {boolean} [props.allowSuggestionOffset=true] - Whether to allow suggestion offset (optional)
 * @returns {JSX.Element} Component JSX
 */
const AddressInputContainer = ({
    addressInputText,
    inputLabel,
    className,
    errors = {},
    allowSuggestionOffset = true,
}) => {
    const {
        state: addressDetailsState,
        dataRequest: addressDetailsDataRequest,
    } = useAddressDetailsContext();

    const {
        state: addressSuggestionsState,
        dataRequest: addressSuggestionsDataRequest,
    } = useAddressSuggestionsContext();

    const addressDetails = getAddressDetails(addressDetailsState);

    const addressSuggestions = getAddressSuggestions(addressSuggestionsState);
    const addressSuggestionsErrors = getErrors(addressSuggestionsState);
    const isLoadingAddressSuggestions = getIsLoading(addressSuggestionsState);

    const onSelectSuggestedAddress = useCallback(
        (placeId) => addressDetailsDataRequest(placeId),
        [addressDetailsDataRequest],
    );

    const onAddressLine1Change = useCallback(
        (value) => addressSuggestionsDataRequest(value),
        [addressSuggestionsDataRequest],
    );

    return (
        <AddressInput
            onAddressLine1Change={onAddressLine1Change}
            addressSuggestions={addressSuggestions}
            addressSuggestionsErrors={addressSuggestionsErrors}
            isLoadingAddressSuggestions={isLoadingAddressSuggestions}
            onSelectSuggestedAddress={onSelectSuggestedAddress}
            addressDetails={addressDetails}
            errors={errors}
            addressInputText={addressInputText}
            inputLabel={inputLabel}
            className={className}
            allowSuggestionOffset={allowSuggestionOffset}
        />
    );
};

export default AddressInputContainer;
